import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import LanguageSwitcher from './ui/LanguageSwitcher';
import { track, appUrlWithId } from '../lib/analytics';

const navLinks = [
  { key: 'home', href: '#home' },
  { key: 'api', href: '#features' },
  { key: 'app', href: '#app' },
  { key: 'developers', href: '#developers' },
  { key: 'faq', href: '#faq' }
];

const Header = () => {
  const { t } = useTranslation();
  const { darkMode, toggleDarkMode } = useTheme();
  const [navBar, setnavBar] = useState("translate-x-full");

  const closeNav = () => setnavBar("translate-x-full");
  const openNav = () => setnavBar("translate-x-0");

  return (
    <>
      <div className="relative z-50 w-full transition bg-transparent border-b border-main border-opacity-[0.15]">
        <div className="mainCont flex items-center justify-between gap-5 py-4">
          {/* Logo */}
          <a href="#home" className="flex w-fit">
            <img src="/images/logo-light.svg" alt="Soroswap" className="w-[120px] dark:hidden" />
            <img src="/images/logo-dark.svg" alt="Soroswap" className="w-[120px] hidden dark:flex" />
          </a>

          {/* Navigation */}
          <div className={`flex items-start lg4:items-center flex-col lg4:flex-row gap-6 text-[15px] lg4:gap-5 fixed top-0 right-0 lg4:relative lg4:top-auto lg4:left-auto px-4 pt-16 pb-5 lg4:px-0 lg4:pb-0 lg4:pt-0 bg-white dark:bg-[#0F1016] dark:lg4:bg-transparent lg4:bg-transparent h-screen lg4:h-auto max-h-screen overflow-y-auto lg4:overflow-visible border-l-none sm2:border-l-[3px] border-main lg4:border-none w-full sm2:w-[300px] lg4:w-auto z-[9999999] transition lg4:transition-none lg4:translate-x-0 ${navBar}`}>
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                onClick={closeNav}
                className={`transition-colors ${
                  darkMode
                    ? 'text-gray-300 hover:text-main'
                    : 'text-gray-700 hover:text-purple-600'
                }`}
              >
                {t(`nav.${link.key}`)}
              </a>
            ))}

            {/* External links */}
            <a
              href="https://info.soroswap.finance"
              onClick={() => {
                track('cta_clicked', {
                  cta_id: 'explore_info',
                  cta_text: t('nav.explore'),
                  section: 'header',
                  destination_url: 'https://info.soroswap.finance',
                  destination_type: 'info'
                });
                closeNav();
              }}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-1 transition-colors ${
                darkMode
                  ? 'text-gray-300 hover:text-main'
                  : 'text-gray-700 hover:text-purple-600'
              }`}
            >
              {t('nav.explore')}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>

            {/* Language switcher */}
            <LanguageSwitcher onSelect={closeNav} />

            {/* Dark mode toggle */}
            <button
              onClick={() => {
                const newMode = !darkMode;
                toggleDarkMode();
                track('theme_toggled', { to: newMode ? 'dark' : 'light' });
                closeNav();
              }}
              className="lg4:-ml-2 -mt-5 lg4:mt-0 p-2 rounded-lg transition-colors hover:bg-main/10"
              aria-label={t('nav.toggleDarkMode')}
            >
              {/* Moon icon (light mode) */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="flex dark:hidden w-5 h-5"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M6 .278a.768.768 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277.527 0 1.04-.055 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278zM4.858 1.311A7.269 7.269 0 0 0 1.025 7.71c0 4.02 3.279 7.276 7.319 7.276a7.316 7.316 0 0 0 5.205-2.162c-.337.042-.68.063-1.029.063-4.61 0-8.343-3.714-8.343-8.29 0-1.167.242-2.278.681-3.286z" />
                <path d="M10.794 3.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387a1.734 1.734 0 0 0-1.097 1.097l-.387 1.162a.217.217 0 0 1-.412 0l-.387-1.162A1.734 1.734 0 0 0 9.31 6.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387a1.734 1.734 0 0 0 1.097-1.097l.387-1.162zM13.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.156 1.156 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.156 1.156 0 0 0-.732-.732l-.774-.258a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732L13.863.1z" />
              </svg>
              {/* Sun icon (dark mode) */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="hidden dark:flex w-6 h-6 text-main"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M8 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0 1a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm.5-9.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0zm0 11a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0zm5-5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm-11 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm9.743-4.036a.5.5 0 1 1-.707-.707.5.5 0 0 1 .707.707zm-7.779 7.779a.5.5 0 1 1-.707-.707.5.5 0 0 1 .707.707zm7.072 0a.5.5 0 1 1 .707-.707.5.5 0 0 1-.707.707zM3.757 4.464a.5.5 0 1 1 .707-.707.5.5 0 0 1-.707.707z" />
              </svg>
            </button>

            {/* Action buttons */}
            <div className="flex flex-col lg4:flex-row items-start lg4:items-center gap-3 mt-4 lg4:mt-0 lg4:ml-2">
              {/* Contact button */}
              <a
                href="#contact"
                onClick={() => {
                  track('cta_clicked', {
                    cta_id: 'contact_header',
                    cta_text: t('nav.contact'),
                    section: 'header',
                    destination_url: '#contact',
                    destination_type: 'anchor'
                  });
                  closeNav();
                }}
                className={`px-4 py-2 rounded-full font-medium transition-all text-sm ${
                  darkMode
                    ? 'border border-white/20 text-white hover:bg-white/10'
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t('nav.contact')}
              </a>

              {/* Launch App button */}
              <a
                href={appUrlWithId('https://app.soroswap.finance')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  track('launch_app_clicked', {
                    cta_id: 'launch_app_header',
                    cta_text: t('nav.launchApp'),
                    section: 'header',
                    destination_url: 'https://app.soroswap.finance',
                    destination_type: 'app'
                  });
                  track('cta_clicked', {
                    cta_id: 'launch_app_header',
                    cta_text: t('nav.launchApp'),
                    section: 'header',
                    destination_url: 'https://app.soroswap.finance',
                    destination_type: 'app'
                  });
                  closeNav();
                }}
                className="px-5 py-2 rounded-full font-medium text-sm bg-main hover:bg-main2 text-white transition-all flex items-center gap-1.5"
              >
                {t('nav.launchApp')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Close button (mobile) */}
            <button
              onClick={closeNav}
              className="flex lg4:hidden items-center justify-center absolute top-3 right-3"
              aria-label={t('nav.closeMenu')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="#8866DD" viewBox="0 0 16 16">
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </div>

          {/* Hamburger menu (mobile) */}
          <button
            onClick={openNav}
            className="flex items-center justify-center lg4:hidden"
            aria-label={t('nav.openMenu')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="#8866DD" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z" />
            </svg>
          </button>
        </div>

        {/* Overlay (mobile) */}
        <div
          onClick={closeNav}
          className={`flex lg4:hidden fixed top-0 left-0 min-h-screen min-w-full bg-black dark:bg-white dark:bg-opacity-30 bg-opacity-30 transition z-[60] ${
            navBar === "translate-x-full" ? "opacity-0 invisible" : "opacity-100 visible"
          }`}
        />
      </div>
    </>
  );
};

export default Header;
